// ── CreateScoutSheet — host creates a Scout event and invites friends/matches ──
import { useState, useEffect } from 'react';
import { useApp } from '../contexts/AppContext';
import { SCOUT_TYPES } from '../utils/scoutAlgo';

const G = {
  bg:'var(--black)', card:'var(--dark)', border:'var(--border)',
  white:'var(--white)', muted:'var(--muted)', gold:'var(--gold)',
  goldPale:'var(--gold-pale)', goldBdr:'var(--gold-border)',
  red:'#E07070', sans:"'Outfit',sans-serif", serif:"'Cormorant Garamond',serif",
};

const VENUE_TYPES = [
  { id:'restaurant',   icon:'🍽️', fr:'Restaurant',    en:'Restaurant'    },
  { id:'bar',          icon:'🍸', fr:'Bar / Cocktails', en:'Bar / Cocktails' },
  { id:'rooftop',      icon:'🌇', fr:'Rooftop',        en:'Rooftop'       },
  { id:'outdoor',      icon:'🌿', fr:'Extérieur',      en:'Outdoor'       },
  { id:'private_room', icon:'🏠', fr:'Salle privée',   en:'Private room'  },
  { id:'surprise',     icon:'✨', fr:'Surprise',       en:'Surprise'      },
];

const TIME_SLOTS = [
  { id:'morning',   icon:'🌅', fr:'Matin',       en:'Morning'   },
  { id:'afternoon', icon:'☀️', fr:'Après-midi',  en:'Afternoon' },
  { id:'evening',   icon:'🌆', fr:'Soirée',      en:'Evening'   },
  { id:'night',     icon:'🌙', fr:'Nuit',        en:'Night'     },
];

function pill(active) {
  return {
    padding:'0.38rem 0.75rem', borderRadius:99, cursor:'pointer', fontFamily:G.sans,
    fontSize:'0.72rem', border:`1px solid ${active ? G.gold : G.border}`,
    background: active ? G.goldPale : 'transparent',
    color: active ? G.gold : G.muted, transition:'all 0.15s',
  };
}

function StepDots({ step, total }) {
  return (
    <div style={{ display:'flex', gap:'0.35rem', justifyContent:'center', marginBottom:'1.3rem' }}>
      {Array.from({ length: total }).map((_, i) => (
        <div key={i} style={{
          width: i === step ? 18 : 6, height:6, borderRadius:99, transition:'width 0.2s',
          background: i === step ? G.gold : i < step ? 'rgba(201,168,76,0.35)' : G.border,
        }}/>
      ))}
    </div>
  );
}

export default function CreateScoutSheet({ onClose, onCreate }) {
  const { currentUser, lang } = useApp();
  const fr = lang === 'fr';

  const [step, setStep] = useState(0);

  // Step 0 — activity
  const [activityType, setActivityType] = useState('dinner');

  // Step 1 — proposed dates (up to 3)
  const [proposedDates, setProposedDates] = useState([{ date: '', time: 'evening' }]);

  // Step 2 — location
  const [venueType,    setVenueType]    = useState('restaurant');
  const [locationArea, setLocationArea] = useState('');

  // Step 3 — invite friends or matches
  const [friends,   setFriends]   = useState([]);
  const [matches,   setMatches]   = useState([]);
  const [loadingFr, setLoadingFr] = useState(false);
  const [selected,  setSelected]  = useState(new Set());

  const isDateType = activityType === 'date';

  const [sending, setSending] = useState(false);
  const [error,   setError]   = useState(null);

  // Fetch friends + matches on mount
  useEffect(() => {
    if (!currentUser?.id) return;
    setLoadingFr(true);
    import('../utils/supabase').then(async ({ supabase }) => {
      const [{ data: ratedMe }, { data: iRated }, { data: myMatches }] = await Promise.all([
        supabase.from('friend_ratings').select('rater_id').eq('target_id', currentUser.id).not('rater_id', 'is', null),
        supabase.from('friend_ratings').select('target_id').eq('rater_id', currentUser.id),
        supabase.from('matches').select('user_a, user_b').or(`user_a.eq.${currentUser.id},user_b.eq.${currentUser.id}`).limit(50),
      ]);
      const friendIds = [...new Set([
        ...(ratedMe || []).map(r => r.rater_id),
        ...(iRated  || []).map(r => r.target_id),
      ].filter(Boolean))];
      if (friendIds.length) {
        const { data } = await supabase.from('users').select('id, name, age, photos, job').in('id', friendIds);
        setFriends(data || []);
      }
      const matchIds = [...new Set(
        (myMatches || []).map(m => m.user_a === currentUser.id ? m.user_b : m.user_a).filter(Boolean)
      )];
      if (matchIds.length) {
        const { data } = await supabase.from('users').select('id, name, age, photos, job').in('id', matchIds);
        setMatches(data || []);
      }
      setLoadingFr(false);
    });
  }, [currentUser?.id]);

  // Date slot helpers
  const addSlot = () => {
    if (proposedDates.length >= 3) return;
    setProposedDates(prev => [...prev, { date: '', time: 'evening' }]);
  };
  const removeSlot = (i) => setProposedDates(prev => prev.filter((_, idx) => idx !== i));
  const updateSlot = (i, key, val) =>
    setProposedDates(prev => prev.map((s, idx) => idx === i ? { ...s, [key]: val } : s));

  const toggleFriend = (id) => setSelected(prev => {
    const next = new Set(prev);
    if (next.has(id)) {
      next.delete(id);
    } else {
      if (isDateType) next.clear(); // date = 1 person max
      next.add(id);
    }
    return next;
  });

  const canNext = () => {
    if (step === 0) return !!activityType;
    if (step === 1) return proposedDates.some(s => s.date);
    if (step === 2) return !!venueType;
    if (step === 3) return selected.size > 0;
    return false;
  };

  const submit = async () => {
    if (!currentUser?.id) return;
    setSending(true);
    setError(null);
    try {
      const { supabase } = await import('../utils/supabase');
      const validDates = proposedDates.filter(s => s.date);

      const { data: scout, error: err } = await supabase
        .from('scouts')
        .insert({
          host_id:        currentUser.id,
          network_id:     currentUser.network_id || 'ca_montreal',
          type:           activityType,
          activity_type:  activityType,
          proposed_dates: validDates,
          venue_type:     venueType,
          location_area:  locationArea.trim() || null,
          is_private:     true,
          status:         'waiting',
        })
        .select('id')
        .single();

      if (err) throw err;

      const invitations = [...selected].map(invitee_id => ({
        scout_id:  scout.id,
        invitee_id,
        invited_by: currentUser.id,
      }));

      const { error: invErr } = await supabase.from('scout_invitations').insert(invitations);
      if (invErr) throw invErr;

      onCreate?.(scout);
    } catch (e) {
      setError(e.message || 'Error');
      setSending(false);
    }
  };

  const scoutTypes = Object.entries(SCOUT_TYPES);

  // ── Today's date as min for date inputs
  const today = new Date().toISOString().split('T')[0];

  return (
    <div className="overlay" onClick={onClose}>
      <div className="sheet" onClick={e => e.stopPropagation()} style={{ maxHeight:'88vh' }}>

        {/* Handle */}
        <div style={{ textAlign:'center', padding:'0.6rem 0 0' }}>
          <div style={{ width:32, height:2, background:'var(--muted2)', margin:'0 auto', borderRadius:99 }}/>
        </div>

        <div style={{ padding:'1.2rem 1.2rem 2rem', overflowY:'auto' }}>

          {/* Title */}
          <div style={{ marginBottom:'1.2rem' }}>
            <div style={{ fontFamily:G.serif, fontSize:'1.4rem', color:G.white, fontWeight:400, marginBottom:'0.15rem' }}>
              {isDateType
                ? (fr ? 'Proposer un rendez-vous' : 'Propose a date')
                : (fr ? 'Créer un Scout privé' : 'Create a private Scout')}
            </div>
            <div style={{ fontSize:'0.68rem', color:G.muted }}>
              {isDateType
                ? (fr ? 'Invite l\'un de tes matchs BeeLove à te rencontrer' : 'Invite one of your BeeLove matches to meet you')
                : (fr ? 'Organise un événement avec tes amis' : 'Organise an event with your friends')}
            </div>
          </div>

          <StepDots step={step} total={4}/>

          {/* ── Step 0 — Activity ── */}
          {step === 0 && (() => {
            const TYPE_LABELS_FR = { dinner:'Dîner', apero:'Apéro', coffee:'Café', outdoor:'Sortie nature', culture:'Culture', board_games:'Jeux', sport:'Sport', workshop:'Atelier', date:'Rendez-vous', mixer:'Mixer' };
            const TYPE_LABELS_EN = { dinner:'Dinner', apero:'Apéro', coffee:'Coffee', outdoor:'Outdoor', culture:'Culture', board_games:'Board games', sport:'Sport', workshop:'Workshop', date:'Date', mixer:'Mixer' };
            const regularTypes = scoutTypes.filter(([id]) => !['date','mixer'].includes(id));
            const specialTypes = scoutTypes.filter(([id]) => ['date','mixer'].includes(id));
            return (
              <>
                <p style={{ fontSize:'0.62rem', color:G.muted, textTransform:'uppercase', letterSpacing:'0.1em', marginBottom:'0.7rem' }}>
                  {fr ? 'Type d\'activité' : 'Activity type'}
                </p>
                <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'0.45rem', marginBottom:'0.8rem' }}>
                  {regularTypes.map(([id, ct]) => (
                    <button key={id} onClick={() => setActivityType(id)}
                      style={{
                        padding:'0.6rem 0.75rem', background: activityType===id ? G.goldPale : 'transparent',
                        border:`1px solid ${activityType===id ? G.gold : G.border}`,
                        borderRadius:10, color: activityType===id ? G.gold : G.muted,
                        fontFamily:G.sans, fontSize:'0.75rem', cursor:'pointer',
                        display:'flex', alignItems:'center', gap:'0.45rem', textAlign:'left',
                      }}>
                      <span style={{ fontSize:'1rem' }}>{ct.icon}</span>
                      <span>{fr ? TYPE_LABELS_FR[id] : TYPE_LABELS_EN[id]}</span>
                    </button>
                  ))}
                </div>
                <p style={{ fontSize:'0.6rem', color:G.muted, textTransform:'uppercase', letterSpacing:'0.1em', marginBottom:'0.5rem', marginTop:'0.4rem', borderTop:`1px solid ${G.border}`, paddingTop:'0.8rem' }}>
                  {fr ? 'Rencontres & matchs' : 'Dating & matches'}
                </p>
                <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'0.45rem', marginBottom:'0.8rem' }}>
                  {specialTypes.map(([id, ct]) => (
                    <button key={id} onClick={() => setActivityType(id)}
                      style={{
                        padding:'0.6rem 0.75rem',
                        background: activityType===id ? 'rgba(201,168,76,0.15)' : 'rgba(201,168,76,0.03)',
                        border:`1px solid ${activityType===id ? G.gold : G.goldBdr}`,
                        borderRadius:10, color: activityType===id ? G.gold : G.muted,
                        fontFamily:G.sans, fontSize:'0.75rem', cursor:'pointer',
                        display:'flex', alignItems:'center', gap:'0.45rem', textAlign:'left',
                      }}>
                      <span style={{ fontSize:'1rem' }}>{ct.icon}</span>
                      <div>
                        <div>{fr ? TYPE_LABELS_FR[id] : TYPE_LABELS_EN[id]}</div>
                        <div style={{ fontSize:'0.6rem', color:G.muted, marginTop:'0.1rem' }}>
                          {id==='date' ? (fr ? 'Invite un match BeeLove' : 'Invite a BeeLove match') : (fr ? 'Mixer de singles' : 'Singles mixer')}
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              </>
            );
          })()}

          {/* ── Step 1 — Proposed dates ── */}
          {step === 1 && (
            <>
              <p style={{ fontSize:'0.62rem', color:G.muted, textTransform:'uppercase', letterSpacing:'0.1em', marginBottom:'0.7rem' }}>
                {fr ? 'Propose jusqu\'à 3 créneaux' : 'Propose up to 3 time slots'}
              </p>
              {proposedDates.map((slot, i) => (
                <div key={i} style={{ background:G.card, border:`1px solid ${G.border}`, borderRadius:10, padding:'0.8rem', marginBottom:'0.55rem' }}>
                  <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:'0.6rem' }}>
                    <span style={{ fontSize:'0.68rem', color:G.muted }}>
                      {fr ? `Créneau ${i+1}` : `Slot ${i+1}`}
                    </span>
                    {proposedDates.length > 1 && (
                      <button onClick={() => removeSlot(i)}
                        style={{ background:'none', border:'none', color:G.muted, cursor:'pointer', fontSize:'0.75rem', padding:0 }}>✕</button>
                    )}
                  </div>
                  <input
                    type="date" min={today}
                    value={slot.date}
                    onChange={e => updateSlot(i, 'date', e.target.value)}
                    style={{ width:'100%', padding:'0.5rem 0.6rem', background:'var(--mid)', border:`1px solid ${G.border}`, borderRadius:8, color:G.white, fontFamily:G.sans, fontSize:'0.8rem', marginBottom:'0.5rem', boxSizing:'border-box', colorScheme:'dark' }}
                  />
                  <div style={{ display:'flex', gap:'0.35rem', flexWrap:'wrap' }}>
                    {TIME_SLOTS.map(t => (
                      <button key={t.id} onClick={() => updateSlot(i, 'time', t.id)}
                        style={pill(slot.time === t.id)}>
                        {t.icon} {fr ? t.fr : t.en}
                      </button>
                    ))}
                  </div>
                </div>
              ))}
              {proposedDates.length < 3 && (
                <button onClick={addSlot}
                  style={{ width:'100%', padding:'0.55rem', border:`1px dashed ${G.border}`, borderRadius:10, background:'transparent', color:G.muted, fontFamily:G.sans, fontSize:'0.75rem', cursor:'pointer', marginBottom:'0.5rem' }}>
                  + {fr ? 'Ajouter un créneau' : 'Add a slot'}
                </button>
              )}
            </>
          )}

          {/* ── Step 2 — Location ── */}
          {step === 2 && (
            <>
              <p style={{ fontSize:'0.62rem', color:G.muted, textTransform:'uppercase', letterSpacing:'0.1em', marginBottom:'0.7rem' }}>
                {fr ? 'Type de lieu' : 'Venue type'}
              </p>
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:'0.4rem', marginBottom:'1.2rem' }}>
                {VENUE_TYPES.map(v => (
                  <button key={v.id} onClick={() => setVenueType(v.id)}
                    style={{
                      padding:'0.6rem 0.4rem', background: venueType===v.id ? G.goldPale : 'transparent',
                      border:`1px solid ${venueType===v.id ? G.gold : G.border}`,
                      borderRadius:10, color: venueType===v.id ? G.gold : G.muted,
                      fontFamily:G.sans, fontSize:'0.68rem', cursor:'pointer',
                      display:'flex', flexDirection:'column', alignItems:'center', gap:'0.3rem',
                    }}>
                    <span style={{ fontSize:'1.1rem' }}>{v.icon}</span>
                    <span>{fr ? v.fr : v.en}</span>
                  </button>
                ))}
              </div>

              <p style={{ fontSize:'0.62rem', color:G.muted, textTransform:'uppercase', letterSpacing:'0.1em', marginBottom:'0.45rem' }}>
                {fr ? 'Zone / quartier (optionnel)' : 'Area / neighbourhood (optional)'}
              </p>
              <input
                type="text"
                value={locationArea}
                onChange={e => setLocationArea(e.target.value)}
                placeholder={fr ? 'ex: Paris 11e, Montréal centre-ville…' : 'e.g. Shoreditch, Downtown Montreal…'}
                style={{ width:'100%', padding:'0.6rem 0.75rem', background:'var(--mid)', border:`1px solid ${G.border}`, borderRadius:10, color:G.white, fontFamily:G.sans, fontSize:'0.82rem', outline:'none', boxSizing:'border-box' }}
              />
            </>
          )}

          {/* ── Step 3 — Invite friends or match ── */}
          {step === 3 && (() => {
            const list = isDateType ? matches : friends;
            const emptyMsg = isDateType
              ? (fr ? 'Aucun match BeeLove pour l\'instant. Like des profils sur Discover pour créer des matchs.' : 'No BeeLove matches yet. Like profiles on Discover to create matches.')
              : (fr ? 'Aucun ami inscrit sur BeeLove pour l\'instant.' : 'No friends on BeeLove yet.');
            const emptyHint = isDateType
              ? null
              : (fr ? 'Invite-les à te noter — ceux qui s\'inscriront apparaîtront ici.' : 'Invite them to rate you — those who sign up will appear here.');
            return (
              <>
                <p style={{ fontSize:'0.62rem', color:G.muted, textTransform:'uppercase', letterSpacing:'0.1em', marginBottom:'0.7rem' }}>
                  {isDateType
                    ? (fr ? 'Choisis un match à inviter' : 'Choose a match to invite')
                    : (fr ? 'Inviter des amis' : 'Invite friends')}
                  {selected.size > 0 && <span style={{ color:G.gold, marginLeft:'0.5rem' }}>({selected.size})</span>}
                </p>
                {isDateType && (
                  <div style={{ padding:'0.5rem 0.7rem', background:'rgba(201,168,76,0.06)', border:`1px solid ${G.goldBdr}`, borderRadius:8, marginBottom:'0.8rem' }}>
                    <p style={{ fontSize:'0.68rem', color:G.muted, margin:0, lineHeight:1.6 }}>
                      {fr
                        ? 'Le concierge BeeLove réservera une table pour vous deux dans un lieu romantique de ton choix.'
                        : 'The BeeLove concierge will book a table for two at a romantic venue of your choice.'}
                    </p>
                  </div>
                )}
                {loadingFr ? (
                  <p style={{ textAlign:'center', color:G.muted, fontSize:'0.8rem', padding:'1.5rem 0' }}>
                    {fr ? 'Chargement…' : 'Loading…'}
                  </p>
                ) : list.length === 0 ? (
                  <div style={{ textAlign:'center', padding:'1rem 0' }}>
                    <p style={{ color:G.muted, fontSize:'0.8rem', lineHeight:1.6 }}>{emptyMsg}</p>
                    {emptyHint && <p style={{ color:'var(--muted2)', fontSize:'0.72rem', marginTop:'0.4rem', lineHeight:1.5 }}>{emptyHint}</p>}
                  </div>
                ) : (
                  <div style={{ display:'flex', flexDirection:'column', gap:'0.4rem', maxHeight:280, overflowY:'auto', marginBottom:'0.8rem' }}>
                    {list.map(f => {
                      const active = selected.has(f.id);
                      return (
                        <button key={f.id} onClick={() => toggleFriend(f.id)}
                          style={{
                            display:'flex', alignItems:'center', gap:'0.7rem',
                            padding:'0.6rem 0.8rem', textAlign:'left', width:'100%',
                            background: active ? G.goldPale : G.card,
                            border:`1px solid ${active ? G.gold : G.border}`,
                            borderRadius:10, cursor:'pointer', transition:'all 0.15s',
                          }}>
                          <div className="avatar av-sm av-round" style={{ flexShrink:0, background:'var(--mid)' }}>
                            {f.photos?.[0]
                              ? <img src={f.photos[0]} alt={f.name}/>
                              : <span style={{ display:'flex', alignItems:'center', justifyContent:'center', width:'100%', height:'100%', fontSize:'0.85rem', color:G.muted }}>{f.name?.[0]}</span>}
                          </div>
                          <div style={{ flex:1, minWidth:0 }}>
                            <div style={{ fontSize:'0.85rem', color:G.white, fontWeight:500 }}>{f.name}{f.age ? `, ${f.age}` : ''}</div>
                            {f.job && <div style={{ fontSize:'0.65rem', color:G.muted, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{f.job}</div>}
                          </div>
                          {active && (
                            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke={G.gold} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                              <polyline points="20 6 9 17 4 12"/>
                            </svg>
                          )}
                        </button>
                      );
                    })}
                  </div>
                )}
              </>
            );
          })()}

          {error && (
            <p style={{ fontSize:'0.75rem', color:G.red, marginBottom:'0.7rem' }}>{error}</p>
          )}

          {/* Navigation */}
          <div style={{ display:'flex', gap:'0.6rem', marginTop:'1rem' }}>
            {step > 0 && (
              <button onClick={() => setStep(s => s - 1)}
                className="btn btn-outline btn-md" style={{ flex:1 }}>
                {fr ? 'Retour' : 'Back'}
              </button>
            )}
            {step < 3 ? (
              <button onClick={() => setStep(s => s + 1)}
                disabled={!canNext()}
                className="btn btn-gold btn-md"
                style={{ flex: step > 0 ? 2 : 1, opacity: canNext() ? 1 : 0.4 }}>
                {fr ? 'Continuer' : 'Continue'}
              </button>
            ) : (
              <button onClick={submit}
                disabled={!canNext() || sending}
                className="btn btn-gold btn-md"
                style={{ flex:2, opacity: (canNext() && !sending) ? 1 : 0.4 }}>
                {sending
                  ? (fr ? 'Création…' : 'Creating…')
                  : isDateType
                    ? (fr ? 'Proposer le rendez-vous' : 'Propose the date')
                    : (fr ? 'Créer et inviter' : 'Create & invite')}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
